import React, { FC, useMemo } from 'react';
import some from 'lodash/some';
import uniq from 'lodash/uniq';
import { getInitialPermissionContext } from 'utils/permission.utils';
import { PermissionLevels, PoliciesPermissionSetItems } from 'common/constants/permissions';
import PermissionContext, { TPermissionContext } from 'common/contexts/PermissionContext';
import useRootSelector from 'common/hooks/useRootSelector';
import { getArray } from 'common/utils/typeUtils';
import {
  selectCurrentUser,
  selectUserSelectedOrganization,
} from 'modules/authentication/state/selectors';
import {
  IUserProfileInfo,
  IUserProfileRole,
  IUserProfileSecurityRole,
} from 'modules/authentication/interfaces';

interface IPermissionContextProvider {
  skip?: boolean;
  children: JSX.Element;
}

const PermissionContextProvider: FC<IPermissionContextProvider> = ({ skip = false, children }) => {
  const user: IUserProfileInfo = useRootSelector(selectCurrentUser)?.toJS();
  const selectedOrganization = useRootSelector(selectUserSelectedOrganization)?.toJS();

  const contextValue = useMemo((): TPermissionContext | null => {
    if (!skip && user) {
      const permissionContext = getInitialPermissionContext();
      const permissionEntries = Object.entries(PoliciesPermissionSetItems);
      let permissions: PoliciesPermissionSetItems[] = [];

      if (user.level === PermissionLevels.PEAK) {
        const roles = getArray<IUserProfileSecurityRole>(user.securityRoleDtoList);

        for (const role of roles) {
          for (const securityPolicy of role.securityPolicyDtoSet) {
            permissions.push(...securityPolicy.permissionSet);
          }
        }
      } else {
        if (selectedOrganization) {
          const orgLevel = selectedOrganization.permissionsLevel;
          const roles = getArray<IUserProfileRole>(user.securityRoles);

          for (const role of roles) {
            const roleLevel = role.securityRole.level;

            if (roleLevel === PermissionLevels.CORPORATE) {
              for (const securityPolicy of role.securityRole.securityPolicyDtoSet) {
                permissions.push(...securityPolicy.permissionSet);
              }
            }

            if (roleLevel === orgLevel) {
              if (roleLevel === PermissionLevels.CLIENT) {
                const hasClub = some(role.clubList, item => item.id === selectedOrganization.id);

                if (hasClub) {
                  for (const securityPolicy of role.securityRole.securityPolicyDtoSet) {
                    permissions.push(...securityPolicy.permissionSet);
                  }
                }
              }
            }
          }
        }
      }

      permissions = uniq(permissions);

      for (const permission of permissions) {
        let pKey: string | null = null;

        for (const [key, val] of permissionEntries) {
          if (val === permission) {
            pKey = key;
            break;
          }
        }

        if (pKey) {
          permissionContext[pKey] = true;
        }
      }

      permissionContext.PEAK_ORGANIZATION_SIGN_IN_AS_SUPPORT_USER = true; // TODO-3581: Implement
      permissionContext.CORPORATE_SIGN_IN_AS_SUPPORT_USER = true; // TODO-3581: Implement
      permissionContext.PEAK_SETTINGS_FEE_RATES_VIEW = true; // TODO-3667: Implement
      permissionContext.PEAK_SETTINGS_FEE_RATES_EDIT = true; // TODO-3667: Implement
      permissionContext.PEAK_REMITTANCE_VIEW = true; // TODO-3533: Implement
      permissionContext.PEAK_REMITTANCE_CALCULATE = true; // TODO-3533: Implement
      permissionContext.PEAK_REMITTANCE_CANCEL = true; // TODO-3533: Implement
      permissionContext.PEAK_REMITTANCE_DOWNLOAD = true; // TODO-3533: Implement
      permissionContext.PEAK_REMITTANCE_SEND = true; // TODO-3533: Implement
      permissionContext.PEAK_REMITTANCE_FINALIZE = true; // TODO-3533: Implement
      permissionContext.CHARGE_PAYMENT_ANALYSIS_REPORT_VIEW = true; // TODO-3533: Implement
      permissionContext.CHARGE_PAYMENT_ANALYSIS_REPORT_EXPORT = true; // TODO-3533: Implement
      permissionContext.FINANCIAL_SUMMARY_REPORT_VIEW = true; // TODO-3533: Implement
      permissionContext.FINANCIAL_SUMMARY_REPORT_EXPORT = true; // TODO-3533: Implement
      permissionContext.DEPOSIT_STATEMENT_REPORT_VIEW = true; // TODO-3533: Implement
      permissionContext.DEPOSIT_STATEMENT_REPORT_EXPORT = true; // TODO-3533: Implement|
      permissionContext.PEAK_CLIENT_ADJUSTMENTS_VIEW = true; // TODO-3973: Implement
      permissionContext.PEAK_CLIENT_ADJUSTMENTS_EXPORT = true; // TODO-3973: Implement
      permissionContext.PEAK_RECURRING_CLIENT_ADJUSTMENTS_VIEW = true; // TODO-3973: Implement
      permissionContext.PEAK_RECURRING_CLIENT_ADJUSTMENTS_EXPORT = true; // TODO-3973: Implement
      permissionContext.PEAK_ADJUSTMENT_CATEGORY_VIEW = true; // TODO-3973: Implement
      permissionContext.PEAK_ADJUSTMENT_CATEGORY_CREATE = true; // TODO-3973: Implement
      permissionContext.PEAK_ADJUSTMENT_CATEGORY_EDIT = true; // TODO-3973: Implement
      permissionContext.CLIENT_SERVICES_VIEW = true; // TODO-4132: Implement
      permissionContext.CLIENT_SERVICES_EDIT = true; // TODO-4132: Implement
      permissionContext.CORPORATE_SERVICES_VIEW = true; // TODO-4132: Implement
      permissionContext.CORPORATE_SERVICES_EDIT = true; // TODO-4132: Implement
      permissionContext.PEAK_ORGANIZATION_SERVICES_VIEW = true; // TODO-4132: Implement
      permissionContext.PEAK_ORGANIZATION_SERVICES_EDIT = true; // TODO-4132: Implement
      permissionContext.INVOICE_REPORT_REFUND = true; // TODO-4077: Implement
      permissionContext.TRANSACTION_REPORT_VIEW = true; // TODO-4198: Implement

      return permissionContext;
    }

    return null;
  }, [skip, user, selectedOrganization]);

  if (!contextValue) {
    return children;
  }

  return <PermissionContext.Provider value={contextValue}>{children}</PermissionContext.Provider>;
};

export default PermissionContextProvider;
